import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { ensureBackendAwake, resetBackendWake } from '@utils/wakeBackend'
import {
  BackendWakeContext,
  type BackendWakePhase,
} from '@/contexts/BackendWakeContext'

const SHOW_OVERLAY_AFTER_MS = 600

type BackendWakeGateProps = {
  children: React.ReactNode
  /** Skip wake (unit tests / Storybook). Defaults to true in Vitest. */
  skip?: boolean
}

/**
 * Ensures the Fly.io backend is awake before treating the app as ready.
 * While waking, children stay mounted (scrollable); Layout shows BackendWakeBanner.
 */
const BackendWakeGate: React.FC<BackendWakeGateProps> = ({
  children,
  skip = import.meta.env.MODE === 'test',
}) => {
  const [status, setStatus] = useState<BackendWakePhase>(skip ? 'ready' : 'checking')
  const [showBanner, setShowBanner] = useState(false)

  const runWake = useCallback(async () => {
    setStatus('checking')
    setShowBanner(false)
    const ok = await ensureBackendAwake()
    setStatus(ok ? 'ready' : 'failed')
  }, [])

  useEffect(() => {
    if (skip) return
    void runWake()
  }, [skip, runWake])

  useEffect(() => {
    if (status !== 'checking') return
    const timer = window.setTimeout(() => setShowBanner(true), SHOW_OVERLAY_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [status])

  const handleRetry = () => {
    resetBackendWake()
    void runWake()
  }

  const contextValue = useMemo(
    () => ({
      phase: status,
      showWakeBanner: status === 'checking' && showBanner,
    }),
    [status, showBanner],
  )

  if (status === 'failed') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          px: 3,
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          Could not reach the server
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          The backend may still be starting after idle time. Wait a moment and try again.
        </Typography>
        <Button variant="contained" onClick={handleRetry}>
          Try again
        </Button>
      </Box>
    )
  }

  return (
    <BackendWakeContext.Provider value={contextValue}>
      {children}
    </BackendWakeContext.Provider>
  )
}

export default BackendWakeGate
